import { useEffect, useRef, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';

// ─── Route manifest ─────────────────────────────────────────────

/**
 * Central route → lazy import mapping.
 * Priority: 1 = critical, 2 = common, 3 = secondary
 */
const ROUTE_MANIFEST = {
  '/dashboard':          { loader: () => import('../pages/Dashboard'),             priority: 1 },
  '/inventory/products': { loader: () => import('../pages/Inventory/Products'),    priority: 1 },
  '/orders':             { loader: () => import('../pages/Orders'),                priority: 1 },
  '/analytics':          { loader: () => import('../pages/Analytics/Analytics'),   priority: 2 },
  '/suppliers':          { loader: () => import('../pages/Suppliers'),             priority: 2 },
  '/inventory/detail':   { loader: () => import('../pages/Inventory/ProductDetail'), priority: 3 },
};

// ─── Prefetch budget / tracking ─────────────────────────────────

const _prefetched = new Set();
const _inflight = new Set();
const MAX_CONCURRENT = 2;

/**
 * Check network quality — skip prefetch on slow connections
 */
const shouldPrefetch = () => {
  if (typeof navigator === 'undefined') return true;
  if (navigator.connection) {
    const conn = navigator.connection;
    if (conn.saveData) return false;
    if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') return false;
  }
  return navigator.onLine !== false;
};

/**
 * Load a route by path (deduped, budget-aware)
 */
const prefetchRoute = (path) => {
  if (_prefetched.has(path) || _inflight.size >= MAX_CONCURRENT) return;
  if (!shouldPrefetch()) return;

  // Find matching manifest entry (prefix match)
  const entry = Object.entries(ROUTE_MANIFEST).find(([key]) => path.startsWith(key));
  const loader = entry?.[1]?.loader;
  if (!loader) return;

  _inflight.add(path);
  loader()
    .then(() => {
      _prefetched.add(path);
    })
    .catch(() => {
      // Silently fail — will load on demand
    })
    .finally(() => {
      _inflight.delete(path);
    });
};

// ─── usePrefetchStrategy ────────────────────────────────────────

/**
 * Prefetch Strategy Hook
 * Intelligently preloads routes based on current location.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const usePrefetchStrategy = () => {
  const location = useLocation();

  useEffect(() => {
    if (!shouldPrefetch()) return;

    const preloadTimer = setTimeout(() => {
      const path = location.pathname;

      // Auth pages → preload dashboard
      if (path.includes('login') || path.includes('password')) {
        prefetchRoute('/dashboard');
      }

      // Dashboard → preload most-common next pages
      if (path === '/dashboard' || path === '/') {
        prefetchRoute('/inventory/products');
        prefetchRoute('/orders');
      }

      // Products list → preload detail
      if (path.includes('/inventory/products')) {
        prefetchRoute('/inventory/detail');
      }

      // Any analytics context → preload analytics
      if (path.includes('/analytics') || path.includes('/dashboard')) {
        prefetchRoute('/analytics');
      }
    }, 1500); // Wait 1.5s after navigation settles

    return () => clearTimeout(preloadTimer);
  }, [location.pathname]);
};

// ─── Preload critical routes ────────────────────────────────────

/**
 * Preload critical routes immediately after app initialization
 */
// eslint-disable-next-line react-refresh/only-export-components
export const preloadCriticalRoutes = () => {
  requestIdleCallback(
    () => {
      // Only prefetch priority-1 routes
      Object.entries(ROUTE_MANIFEST)
        .filter(([, v]) => v.priority === 1)
        .forEach(([path]) => prefetchRoute(path));
    },
    { timeout: 3000 }
  );
};

// ─── PrefetchLink ───────────────────────────────────────────────

/**
 * Link that preloads its target route on hover (desktop) or touchstart (mobile).
 *
 * Usage: <PrefetchLink to="/dashboard">Dashboard</PrefetchLink>
 *
 * @param {object} props
 * @param {string} props.to - Route path
 * @param {boolean} [props.prefetch=true] - Enable/disable prefetch
 * @param {'hover'|'viewport'|'none'} [props.strategy='hover'] - When to trigger
 */
export const PrefetchLink = ({
  to,
  children,
  prefetch = true,
  strategy = 'hover',
  ...props
}) => {
  const ref = useRef(null);

  const triggerPrefetch = useCallback(() => {
    if (!prefetch || !shouldPrefetch()) return;
    prefetchRoute(to);
  }, [to, prefetch]);

  // Viewport-based prefetch via IntersectionObserver
  useEffect(() => {
    if (strategy !== 'viewport' || !prefetch || !ref.current) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          triggerPrefetch();
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [strategy, prefetch, triggerPrefetch]);

  const handleMouseEnter = strategy === 'hover' ? triggerPrefetch : undefined;
  const handleTouchStart = strategy === 'hover' ? triggerPrefetch : undefined;

  return (
    <Link
      ref={ref}
      to={to}
      onMouseEnter={handleMouseEnter}
      onTouchStart={handleTouchStart}
      {...props}
    >
      {children}
    </Link>
  );
};

// ─── Resource hints ─────────────────────────────────────────────

/**
 * Add DNS-prefetch / preconnect hints for external origins
 * Call once in app init.
 * @param {string[]} origins - e.g., ['https://api.example.com', 'https://cdn.example.com']
 */
// eslint-disable-next-line react-refresh/only-export-components
export const addResourceHints = (origins = []) => {
  if (typeof document === 'undefined') return;

  origins.forEach((origin) => {
    // dns-prefetch
    if (!document.querySelector(`link[rel="dns-prefetch"][href="${origin}"]`)) {
      const dns = document.createElement('link');
      dns.rel = 'dns-prefetch';
      dns.href = origin;
      document.head.appendChild(dns);
    }
    // preconnect
    if (!document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
      const pc = document.createElement('link');
      pc.rel = 'preconnect';
      pc.href = origin;
      pc.crossOrigin = 'anonymous';
      document.head.appendChild(pc);
    }
  });
};

// ─── Diagnostics ────────────────────────────────────────────────

/**
 * Get prefetch statistics
 */
// eslint-disable-next-line react-refresh/only-export-components
export const getPrefetchStats = () => ({
  prefetched: [..._prefetched],
  inflight: [..._inflight],
  totalPrefetched: _prefetched.size,
  availableRoutes: Object.keys(ROUTE_MANIFEST),
  networkOk: shouldPrefetch(),
});

// ─── Polyfill ───────────────────────────────────────────────────

if (typeof window !== 'undefined' && !window.requestIdleCallback) {
  window.requestIdleCallback = (cb) => {
    const start = Date.now();
    return setTimeout(() => {
      cb({
        didTimeout: false,
        timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
      });
    }, 1);
  };
}
